import { LABELS, TOOLTIPS, URL_PATHS } from '@constants'
import { yupResolver } from '@hookform/resolvers/yup'
import { useStore } from '@hooks/useStore'
import { CheckCircle, Error } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  CardActions,
  CardContent,
  CardHeader,
  Collapse,
  Divider,
  FormHelperText,
  Tooltip,
  Typography,
} from '@mui/material'
import { OvCard } from '@components'
import { IOVErrorResponseData, IUserRegister } from '@typings'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { OvFormInput } from 'src/components/core/ov-form-input/ov-form-input'
import { OvFormProvider } from 'src/components/core/ov-form-provider/ov-form-provider'
import { OvForm } from 'src/components/core/ov-form/ov-form'
import styles from './register.form.module.scss'
import { validationSchema } from './validation/register.form.validation'

export const RegisterForm = observer(() => {
  const { auth } = useStore()
  const [formError, setFormError] = useState<string | null>(null)
  const [registrationSuccess, setRegistrationSuccess] = useState(false)
  const [registeredEmail, setRegisteredEmail] = useState<string>('')
  const navigate = useNavigate()

  const form = useForm<IUserRegister>({
    resolver: yupResolver(validationSchema()),
    defaultValues: {
      name: undefined,
      email: '',
      password: '',
      password_confirmation: '',
    },
    mode: 'all',
  })

  const { formState } = form

  const handleRegister: SubmitHandler<IUserRegister> = async data => {
    setFormError(null)
    await auth
      .register(data)
      .then(() => {
        setRegisteredEmail(data.email)
        setRegistrationSuccess(true)
      })
      .catch((errors: IOVErrorResponseData) => handleFormErrors(errors))
  }

  const handleFormErrors = (data: IOVErrorResponseData) => {
    const { message, errors } = data

    if (message) setFormError(message)

    if (errors) {
      const errorKeys = Object.keys(errors)

      errorKeys.map(singleKey =>
        form.setError(singleKey as any, {
          type: 'custom',
          message: errors[singleKey][0],
        })
      )
    }
  }

  const sumitForm = () => {
    form.handleSubmit(handleRegister)()
  }

  // Show success message after registration
  if (registrationSuccess) {
    return (
      <OvCard
        className={styles['register-card']}
        elevation={4}
        sx={{ p: 4 }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography
              variant="h4"
              gutterBottom>
              Account Aangemaakt!
            </Typography>
          </Box>
          <Alert
            severity="success"
            sx={{ mb: 3 }}>
            <Typography
              variant="body1"
              gutterBottom>
              Uw account is succesvol aangemaakt. We hebben een verificatie-e-mail gestuurd naar:
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', mb: 2 }}>
              {registeredEmail}
            </Typography>
            <Typography variant="body2">
              Controleer uw inbox en klik op de verificatielink om uw account te activeren. Vergeet niet ook uw
              spam/ongewenste e-mail map te controleren.
            </Typography>
          </Alert>
          <Tooltip title={TOOLTIPS.GO_TO_LOGIN}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => navigate(URL_PATHS.LOGIN)}
              sx={{ mb: 2 }}>
              Ga naar Inloggen
            </Button>
          </Tooltip>
          <Typography
            variant="body2"
            align="center"
            color="text.secondary">
            Geen e-mail ontvangen? Controleer uw spam map of probeer het opnieuw.
          </Typography>
        </CardContent>
      </OvCard>
    )
  }

  return (
    <OvFormProvider
      form={form}
      onSubmit={sumitForm}
      loading={auth.loading}
      enableNavigationBlocker={false}>
      <OvForm>
        <OvCard
          className={styles['register-card']}
          elevation={4}>
          <CardHeader
            title={LABELS.REGISTER_FORM_TITLE}
            sx={{ pb: 1 }}
          />
          <CardContent
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              pt: 2,
            }}>
            <OvFormInput
              name="name"
              inputProps={{ type: 'text' }}
              label={LABELS.NAME}
              required
            />
            <OvFormInput
              name="email"
              inputProps={{ type: 'email' }}
              label={LABELS.EMAIL}
              required
            />
            <OvFormInput
              name="password"
              type="password"
              label={LABELS.PASSWORD}
              required
            />
            <OvFormInput
              name="password_confirmation"
              type="password"
              label={LABELS.PASSWORD_AGAIN}
              required
            />
            <Collapse in={!!formError}>
              <Alert
                color="error"
                icon={<Error />}>
                <FormHelperText error>{formError}</FormHelperText>
              </Alert>
            </Collapse>
          </CardContent>
          <CardActions
            sx={{
              px: 2,
              pb: 2,
              flexDirection: 'column',
              gap: 1,
            }}>
            <Tooltip title={TOOLTIPS.REGISTER_BUTTON}>
              <Button
                disabled={formState.isSubmitting || auth.loading || !formState.isValid}
                variant="contained"
                type="submit"
                fullWidth>
                {LABELS.REGISTER}
              </Button>
            </Tooltip>
          </CardActions>
          <Divider sx={{ '& .MuiDivider-wrapper': { fontFamily: 'Roboto Slab' }, my: 2 }}>
            {LABELS.ALREADY_HAVE_ACCOUNT}
          </Divider>
          <CardActions
            sx={{
              px: 2,
              pb: 2,
              justifyContent: 'center',
            }}>
            <Tooltip title={TOOLTIPS.GO_TO_LOGIN}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => navigate(URL_PATHS.LOGIN)}>
                {LABELS.LOGIN}
              </Button>
            </Tooltip>
          </CardActions>
        </OvCard>
      </OvForm>
    </OvFormProvider>
  )
})
